/* Reef QoL layer for Nasaym Alteeb — sticky add-to-cart, added-to-cart toast, buy now,
   free-shipping region, mobile-bar cart badge + BUCKS currency sheet, pinned quick-buy actions. */
(() => {
  const compact = window.matchMedia('(max-width: 989px)');
  const root = () => window.Shopify?.routes?.root || '/';

  /* BUCKS currency converter: re-run conversion on content we inject (idempotent on their side). */
  let reconvertTimer = 0;
  const reconvert = () => {
    clearTimeout(reconvertTimer);
    reconvertTimer = setTimeout(() => { try { window.bucksCC?.reConvert?.(); } catch { /* app absent */ } }, 80);
  };

  /* ---------------- Added-to-cart toast (instead of auto-opening the drawer) ---------------- */
  const toast = document.querySelector('[data-reef-toast]');
  if (toast) {
    const imageNode = toast.querySelector('[data-toast-image]');
    const productNode = toast.querySelector('[data-toast-product]');
    const timer = toast.querySelector('.reef-toast__timer');
    const duration = Number(toast.dataset.duration) || 5000;
    toast.style.setProperty('--reef-toast-duration', `${duration}ms`);
    let drawerRef = null;
    let fallback = 0;
    const hide = () => { toast.classList.remove('is-visible'); clearTimeout(fallback); };
    const show = (item, drawer) => {
      drawerRef = drawer || document.querySelector('cart-drawer');
      const title = item?.product_title || item?.title || '';
      const variant = item?.variant_title && item.variant_title !== 'Default Title' ? ` — ${item.variant_title}` : '';
      productNode.textContent = `${title}${variant}`.trim();
      if (item?.image) {
        try { const url = new URL(item.image, location.origin); url.searchParams.set('width', '120'); imageNode.src = url.href; } catch { imageNode.src = item.image; }
        imageNode.hidden = false;
      } else { imageNode.hidden = true; imageNode.removeAttribute('src'); }
      toast.classList.remove('is-visible');
      void toast.offsetWidth;
      toast.classList.add('is-visible');
      clearTimeout(fallback);
      fallback = setTimeout(() => { if (!toast.matches(':hover, :focus-within')) hide(); }, duration + 1500);
    };
    timer.addEventListener('animationend', hide);
    toast.querySelector('[data-toast-close]').addEventListener('click', hide);
    toast.querySelector('[data-toast-view-cart]').addEventListener('click', (event) => {
      hide();
      const drawer = drawerRef || document.querySelector('cart-drawer');
      if (drawer?.open) drawer.open(event.currentTarget);
      else location.assign(window.routes?.cart_url || '/cart');
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && toast.classList.contains('is-visible')) hide(); });

    // Patch the cart drawer: refresh its contents after an add, but don't open it.
    customElements.whenDefined('cart-drawer').then(() => {
      const proto = customElements.get('cart-drawer').prototype;
      const original = proto.renderContents;
      proto.renderContents = function (parsedState) {
        if (!parsedState?.sections) return original.call(this, parsedState);
        this.classList.remove('is-empty');
        this.querySelector('.drawer__inner')?.classList.remove('is-empty');
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
          const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
          const html = parsedState.sections[section.id];
          if (!target || !html) return;
          target.innerHTML = this.getSectionInnerHTML(html, section.selector);
        });
        setTimeout(() => { this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this)); });
        show(parsedState, this);
        document.dispatchEvent(new CustomEvent('reef:cart-update'));
      };
    });
  }

  // Dawn's drawer quantity updates throw when a section id is missing from the page.
  customElements.whenDefined('cart-drawer-items').then(() => {
    const proto = customElements.get('cart-drawer-items').prototype;
    const original = proto.getSectionsToRender;
    proto.getSectionsToRender = function () {
      return original.call(this).filter((section) => document.getElementById(section.id));
    };
  });

  /* ---------------- Free shipping: inside Kuwait vs outside (default: outside) ---------------- */
  const SHIP_KEY = 'reef:ship-region';
  let shipRegion = null;
  const applyShipRegion = () => {
    if (!shipRegion) return;
    document.querySelectorAll('[data-reef-ship-bar]').forEach((bar) => {
      bar.dataset.region = shipRegion;
      bar.querySelectorAll('[data-ship-choose]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.shipChoose === shipRegion));
      });
    });
  };
  const setShipRegion = (region, persist) => {
    shipRegion = region === 'kw' ? 'kw' : 'intl';
    if (persist) { try { localStorage.setItem(SHIP_KEY, shipRegion); } catch { /* private mode */ } }
    applyShipRegion();
  };
  document.addEventListener('click', (event) => {
    const choice = event.target.closest('[data-ship-choose]');
    if (choice) setShipRegion(choice.dataset.shipChoose, true);
  });
  let storedRegion = null;
  try { storedRegion = localStorage.getItem(SHIP_KEY); } catch { /* ignore */ }
  setShipRegion(storedRegion || 'intl', false);
  const drawerNode = document.querySelector('cart-drawer');
  if (drawerNode) new MutationObserver(() => { applyShipRegion(); reconvert(); syncBadge(); }).observe(drawerNode, { childList: true, subtree: true });

  /* ---------------- Mobile bar: cart count badge ---------------- */
  const badge = document.querySelector('[data-reef-cart-count]');
  function syncBadge() {
    if (!badge) return;
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (bubble) badge.textContent = bubble.textContent.trim();
    badge.classList.toggle('is-empty', !Number(badge.textContent.trim()));
  }
  if (badge) {
    const bubbleHost = document.getElementById('cart-icon-bubble');
    if (bubbleHost) new MutationObserver(syncBadge).observe(bubbleHost, { childList: true, subtree: true, characterData: true });
    document.addEventListener('reef:cart-update', () => setTimeout(syncBadge, 50));
    syncBadge();
  }

  /* ---------------- Product page: AJAX add + toast, buy now → checkout, sticky bar ---------------- */
  const pdpForm = document.querySelector('[data-reef-product-form] form');
  const buyNow = document.querySelector('[data-reef-buy-now]');
  if (pdpForm && buyNow) {
    buyNow.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (buyNow.disabled || buyNow.classList.contains('loading')) return;
      buyNow.classList.add('loading');
      buyNow.setAttribute('aria-disabled', 'true');
      try {
        const response = await fetch(new URL('cart/add.js', new URL(root(), location.origin)), {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(pdpForm),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'add');
        location.assign(new URL('checkout', new URL(root(), location.origin)).href);
      } catch (error) {
        const wrapper = pdpForm.querySelector('.product-form__error-message-wrapper');
        if (wrapper) { wrapper.hidden = false; wrapper.querySelector('.product-form__error-message').textContent = error.message || 'تعذّرت إضافة المنتج. حاول مرة أخرى.'; }
        buyNow.classList.remove('loading');
        buyNow.removeAttribute('aria-disabled');
      }
    }, true);
  }

  const sticky = document.querySelector('[data-reef-sticky-atc]');
  if (sticky && pdpForm) {
    const actions = pdpForm.querySelector('[data-reef-pdp-actions]');
    const addButton = pdpForm.querySelector('[data-reef-add]') || pdpForm.querySelector('[type="submit"]');
    const button = sticky.querySelector('[data-reef-sticky-submit]');
    let actionsVisible = true;
    const sync = () => {
      const show = compact.matches && !actionsVisible;
      sticky.hidden = !show;
      document.documentElement.classList.toggle('reef-sticky-atc-visible', show);
    };
    if (actions) {
      new IntersectionObserver((entries) => { actionsVisible = entries[0].isIntersecting; sync(); }, { threshold: 0 }).observe(actions);
    }
    compact.addEventListener('change', sync);
    // Mirror loading/disabled state from the page's own add button.
    if (addButton) {
      const mirror = () => {
        button.disabled = addButton.disabled || addButton.getAttribute('aria-disabled') === 'true';
        button.classList.toggle('is-loading', addButton.classList.contains('loading'));
      };
      new MutationObserver(mirror).observe(addButton, { attributes: true, attributeFilter: ['disabled', 'class', 'aria-disabled'] });
      mirror();
    }
    button.addEventListener('click', () => {
      if (button.disabled) return;
      if (addButton) addButton.click();
      else if (typeof pdpForm.requestSubmit === 'function') pdpForm.requestSubmit();
      else pdpForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
    sync();
  }

  /* ---------------- Quick buy / quick view: pin the actions + details in a footer ---------------- */
  const quickBuy = document.getElementById('ReefQuickBuy');
  const quickShell = quickBuy?.querySelector('.reef-quick-buy__shell');
  const quickContent = quickBuy?.querySelector('[data-quick-buy-content]');
  if (quickBuy && quickShell && quickContent) {
    const footer = document.createElement('div');
    footer.className = 'reef-qb-footer';
    const slot = document.createElement('div');
    slot.className = 'reef-qb-footer__actions';
    footer.append(slot);
    const details = quickBuy.querySelector('[data-quick-buy-details]');
    if (details) footer.append(details);
    quickShell.append(footer);
    quickBuy.classList.add('reef-qb-pinned');
    const relocate = () => {
      slot.replaceChildren();
      const form = quickContent.querySelector('form');
      const actionsNode = quickContent.querySelector('.reef-quick-buy__actions');
      if (!form || !actionsNode) return;
      // form.id is shadowed by the <input name="id"> control — use the attribute.
      let formId = form.getAttribute('id');
      if (!formId) { formId = 'ReefQuickBuyForm'; form.setAttribute('id', formId); }
      actionsNode.querySelectorAll('button').forEach((b) => b.setAttribute('form', formId));
      slot.append(actionsNode);
      reconvert();
    };
    new MutationObserver(relocate).observe(quickContent, { childList: true });
    relocate();
  }

  /* ---------------- Mobile bar: BUCKS currency switcher (replaces حسابي when BUCKS is active) ---------------- */
  const currencyButton = document.querySelector('[data-reef-currency]');
  const currencySheet = document.querySelector('[data-reef-currency-sheet]');
  if (currencyButton && currencySheet) {
    const accountLink = document.querySelector('[data-reef-account]');
    const list = currencySheet.querySelector('[data-reef-currency-list]');
    const flag = currencyButton.querySelector('[data-reef-currency-flag]');
    const label = currencyButton.querySelector('[data-reef-currency-label]');
    const checkIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 4 4L20 5"/></svg>';
    const triggers = () => [...document.querySelectorAll('.buckscc-select-options li.converterTriggers')];
    const current = () => document.querySelector('.buckscc-select-styled .bucks-selected')?.textContent.trim() || document.getElementById('bucksSelector')?.value || '';
    const syncButton = () => {
      const code = current();
      if (!code) return;
      flag.className = `hxoFlag hxoFlag-${code}`;
      label.textContent = code;
    };
    const build = () => {
      const items = triggers();
      if (!items.length) return false;
      const code = current();
      list.replaceChildren(...items.map((li) => {
        const rel = li.getAttribute('rel') || '';
        const geo = li.classList.contains('geoCurrency');
        const name = li.querySelector('.bucksItem')?.textContent.trim() || rel;
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'reef-sheet__item';
        item.setAttribute('role', 'listitem');
        item.dataset.rel = rel;
        item.dataset.geo = geo ? '1' : '';
        item.setAttribute('aria-pressed', String(!geo && rel === code));
        item.innerHTML = `<span class="hxoFlag hxoFlag-${rel}"></span><span class="reef-sheet__name">${geo ? (currencySheet.dataset.autoLabel || 'تلقائي حسب موقعك') : name}</span><span class="reef-sheet__check">${checkIcon}</span>`;
        return item;
      }));
      return true;
    };
    let closing = 0;
    const open = () => {
      if (!build()) return;
      clearTimeout(closing);
      currencySheet.hidden = false;
      requestAnimationFrame(() => currencySheet.classList.add('is-open'));
      currencyButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
    };
    const close = () => {
      currencySheet.classList.remove('is-open');
      currencyButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      closing = setTimeout(() => { currencySheet.hidden = true; }, 280);
    };
    currencyButton.addEventListener('click', open);
    currencySheet.addEventListener('click', (event) => {
      if (event.target.closest('[data-reef-currency-close]')) return close();
      const item = event.target.closest('.reef-sheet__item');
      if (!item) return;
      const target = triggers().find((li) => (li.getAttribute('rel') || '') === item.dataset.rel && li.classList.contains('geoCurrency') === Boolean(item.dataset.geo));
      target?.click();
      close();
      setTimeout(syncButton, 400);
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !currencySheet.hidden) close(); });
    const activate = () => {
      const wrapper = document.querySelector('.buckscc-currency-wrapper');
      if (!wrapper || !triggers().length) return false;
      document.documentElement.classList.add('reef-has-bucks');
      currencyButton.hidden = false;
      if (accountLink) accountLink.hidden = true;
      syncButton();
      new MutationObserver(syncButton).observe(wrapper, { childList: true, subtree: true, characterData: true });
      return true;
    };
    if (!activate()) {
      const poll = setInterval(() => { if (activate()) clearInterval(poll); }, 500);
      setTimeout(() => clearInterval(poll), 20000);
    }
  }
})();
